/**
 * Crawler detection, used to keep bot traffic out of client telemetry.
 *
 * ## Why this exists
 *
 * Crawlers throw exceptions no human ever sees and no engineer can act on, and
 * they throw the SAME one forever. Measured on ordo 2026-09-02: Googlebot alone
 * produced 31 of ~280 client exceptions in 24h — 24 `Rejected` plus 7
 * `Script https://app.ordo.com/sw.js load failed`, every one of them this:
 *
 *     Error: Rejected
 *       at ...navigator.serviceWorker.register (<anonymous>:12:648)
 *       at ? (https://app.ordo.com/registerSW.js:1:98)
 *
 * Googlebot does not support service workers, so `register()` rejects by
 * design. There is no fix, so the alert fires permanently.
 *
 * ## Why filter on the client and not on the message
 *
 * `FaroConfig.ignoreErrors` already drops events by message text, but that is
 * the wrong instrument here: a REAL user hitting a genuine `Rejected` or a
 * genuine `sw.js load failed` is a bug we want to see. The thing that makes
 * these events worthless is WHO produced them, not what they say. So this gates
 * on client identity and leaves the message space untouched.
 *
 * Dropping at `initFaro` rather than in `beforeSend` also means a crawler ships
 * no telemetry at all — no sessions, no page views, no measurements — instead of
 * only having its exceptions filtered.
 *
 * ## Why a curated token list and not /bot/i
 *
 * `bot` is a substring of legitimate device user agents — the Cubot phone brand
 * being the common one — so a bare `/bot/i` test silently discards real users'
 * errors, which is strictly worse than the noise it removes. Every token below
 * is a fetcher that identifies itself unambiguously.
 *
 * Deliberately NOT matched, because these are real people in an in-app browser
 * and their errors are real:
 *   - `GSA/…`            Google Search App (observed reporting a genuine
 *                        NotFoundError on ordo, 2026-09-02)
 *   - `FBAN/`, `FBAV/`   Facebook in-app browser (vs `facebookexternalhit`,
 *                        the link fetcher, which IS matched)
 *   - `WhatsApp`, `Pinterest`, `Instagram` — the app's in-app browser carries
 *                        the same token as its link fetcher, so matching them
 *                        would drop users.
 */

/**
 * Self-identifying non-human fetchers: search engines, SEO and archive
 * crawlers, link-preview bots, uptime monitors, and headless automation.
 * Compared case-insensitively as substrings of the user agent.
 */
const CRAWLER_TOKENS: readonly string[] = [
  // Generic self-identification
  'crawler',
  'crawling',
  'spider',
  // Search engines
  'googlebot',
  'googleother',
  'google-inspectiontool',
  'adsbot-google',
  'mediapartners-google',
  'bingbot',
  'bingpreview',
  'slurp',
  'duckduckbot',
  'baiduspider',
  'yandexbot',
  'sogou',
  'exabot',
  'applebot',
  'petalbot',
  'bytespider',
  'amazonbot',
  'ia_archiver',
  // SEO / analysis
  'semrushbot',
  'ahrefsbot',
  'mj12bot',
  'dotbot',
  'dataforseobot',
  'screaming frog',
  // Link preview fetchers (not in-app browsers — see the header note)
  'facebookexternalhit',
  'facebot',
  'twitterbot',
  'linkedinbot',
  'slackbot',
  'discordbot',
  'telegrambot',
  'embedly',
  'redditbot',
  // Uptime / synthetic monitoring
  'uptimerobot',
  'pingdom',
  'statuscake',
  'site24x7',
  'newrelicpinger',
  'gtmetrix',
  'lighthouse',
  'pagespeed',
  // Headless automation
  'headlesschrome',
  'phantomjs',
  'puppeteer',
  'playwright',
]

/**
 * Whether a user agent belongs to a crawler, link fetcher, uptime monitor or
 * headless automation run.
 *
 * A missing or empty user agent returns `false`: absence is not evidence of a
 * bot, and failing open keeps a real user's telemetry flowing.
 */
export function isLikelyCrawler(userAgent: string | undefined | null): boolean {
  if (!userAgent) {
    return false
  }
  const ua = userAgent.toLowerCase()
  return CRAWLER_TOKENS.some((token) => ua.includes(token))
}
